// Lots of micro-components in this file.  May want to give them display names via named functions in the future, but disable errors for now
/* eslint-disable react/display-name */

import { h, Component } from 'preact';
import { calcChange, formatNumber } from '../../lib/util';
import { Icon } from '@cube/blocks';
import { Text } from 'preact-i18n';
import { FinanceSpinner } from '../glyphs';
import { StockChartBare } from '../stock-chart';
import { getGoodPrevClose } from '../../lib/util-stock-price';
import Flag from '../flags';
import config from '../../defaults';
import style from './style';
import cx from 'classnames';
import linkState from 'linkstate';

const INTL_NS = 'stock-info-table';

const FIELD_HEADERS = {
	price: `${INTL_NS}.price`,
	marketCap: `${INTL_NS}.marketCap`,
	symbol: `${INTL_NS}.symbol`,
	symbolWithName: `${INTL_NS}.symbol`,
	name: `${INTL_NS}.name`,
	changeCompound: `${INTL_NS}.changeCompound`,
	changePrice: `${INTL_NS}.changePrice`,
	changePercent: `${INTL_NS}.changePct`,
	oneMonthChangePercent: `${INTL_NS}.oneMonthPct`,
	threeMonthChangePercent: `${INTL_NS}.threeMonthPct`,
	'52WeekChangePercent': `${INTL_NS}.fiftyTwoWeekPct`,
	YTDChangePercent: `${INTL_NS}.ytdChangePct`,
	trash: `${INTL_NS}.trash`,
	volume: `${INTL_NS}.volume`,
	dollarVolume: `${INTL_NS}.dollarVolume`,
	dollarVolumeChange: `${INTL_NS}.dollarVolumeChange`,
	competitors: `${INTL_NS}.competitors`,
	eventDate: `${INTL_NS}.eventDate`,
	split: `${INTL_NS}.split`,
	dividend: `${INTL_NS}.dividend`
};

export default class StockInfoTable extends Component {
	toSymbols(data) {
		return data && data.rows && data.rows.map(r => r && r.SymbolID).join();
	}

	componentWillMount() {
		this.setState({ cursor: this.props.cursor || 0 });
	}

	componentWillReceiveProps({ data, cursor }) {
		if (!this.state.cursor || this.toSymbols(data) !== this.toSymbols(this.props.data)) {
			this.setState({ cursor });
		}
	}

	render({ data, columns, noData, onDelete, showHeaders, showChart, theme, ...props }, { cursor }) {
		let rows = data && data.rows;
		// If we know that there isn't any data, don't render the spinner and resulting 'Data Not Available'
		if (!noData && (!rows || !rows.length)) return <FinanceSpinner />;
		let symbolWithName = columns.indexOf('symbolWithName') === 0;
		return (
			<div class={cx(style.stockInfoTable, props.dataTable && style.dataTable, style[theme])}>
				{showChart &&
					rows[cursor] &&
					<StockChartBare
						symbol={rows[cursor].SymbolID}
						width="375"
						height="150"
						{...config.chart}
					/>}
				<table class={style.financeTable}>
					<StockInfoHeaders {...{ columns, showHeaders }} />
					{ !noData &&
						<tbody class={cx(symbolWithName ? style.alternate2 : style.alternate1)}>
							{rows.reduce( (allRows, row, i) => {
								if (row) {
									allRows.push(
										<StockInfoRow
											showChart={showChart}
											pos={i}
											selected={cursor === i}
											symbol={row.SymbolID}
											{...props}
											{...{ columns, row, onDelete }}
											onMouseOver={linkState(this, 'cursor', 'pos')}
										/>
									);
								}
								if (row && row.Name && symbolWithName) {
									allRows.push(
										<tr class={cx(style.symbolWithName)}><td colspan={columns.length}>{row.Name || '-'}</td></tr>
									);
								}
								return allRows;
							}, [])}
						</tbody>
					}
				</table>
			</div>
		);
	}
}

export const StockInfoHeaders = ({ columns, showHeaders }) => (
	<thead>
		{showHeaders &&
			<tr>
				{columns.map((field, i) => (
					<th>
						{Array.isArray(showHeaders) ? showHeaders[i] : <Text id={FIELD_HEADERS[field]} />}
					</th>
				))}
			</tr>}
	</thead>
);

export class StockInfoRow extends Component {
	handleMouseOver = () => {
		this.props.onMouseOver(this.props);
	};

	// Workaround for the fact that preact-router wraps every <a> tag everywhere without notice / choice,
	// and we do not want that behavior for this component.
	handleClick = ev => {
		ev.stopPropagation();
	};

	render({ columns, row, onmouseover, selected, showChart, ...props }) {
		return (
			<tr
				class={cx(selected && style.selected, showChart && style.image)}
				onMouseOver={this.handleMouseOver}
			>
				{row &&
					columns.map(col => (
						<NamedField name={col === 'symbolWithName' ? 'symbol' : col} row={row} {...props} onClick={this.handleClick} />
					))}
			</tr>
		);
	}
}

/** Renders a named component from FIELD_COMPONENTS */
const NamedField = ({ name, ...props }) => {
	let goodPrevClose = getGoodPrevClose(props.row);
	props.change = calcChange(props.row.Price, goodPrevClose);
	let Comp = FIELD_COMPONENTS[name];
	return <Comp {...props} />;
};

const createChangePercentComponent = (className, prevField) => ({ onClick, row }) => {
	let cl = cx(style.change, style[className]), inner = '-';

	if (row.Price > 0 && row[prevField] > 0) {
		let change = calcChange(row.Price, row[prevField]);
		cl = cx(cl, style[change.class]);
		inner = formatNumber(change.percent, { Plus: true, Decimals: 2, Default: '0.00%', Suffix: '%' });
	}
	return <td class={cl} onClick={onClick}>{inner}</td>;
};

const maxDecimals = 2;
const minDecimals = 2;

const FIELD_COMPONENTS = {
	flag: ({ row, flags, pos, change, ...props }) => (
		<td class={style.flag} {...props}>
			<Flag country={flags && flags[pos]} />
		</td>
	),

	eventDate: ({ row }) => (
		<td class={style.price}>
			{row.eventDate}
		</td>
	),

	split: ({ row }) => (
		<td class={style.price}>
			{row.split}
		</td>
	),

	dividend: ({ row }) => (
		<td class={style.price}>
			{row.dividend}
		</td>
	),

	price: ({ onClick, row }) => (
		<td class={style.price} onClick={onClick}>
			{formatNumber(row.Price, {
				Comma: true,
				Decimals: row.Price < 10 ? maxDecimals : minDecimals,
				Default: '0.00'
			})}
		</td>
	),

	marketCap: ({ row }) => (
		<td class={style.cap}>
			{formatNumber(row.MarketCap, { Comma: true, Compact: true, Default: '0' })}
		</td>
	),

	symbol: ({ onClick, row, change, ...props }) => {
		let name = props.abbrv ? row.Symbol : (row.ShortName || row.Name || row.Symbol);
		return (
			<td onClick={onClick} class={style.symbol}>
				<a href={`/finance/category/stockdetail/symbol/${row.UniqueSymbol}`}
					data-tracking-text={row.UniqueSymbol}
				>
					{ name || 'Unknown'}
				</a>
			</td>
		);
	},

	name: ({ onClick, row, ...props }) => (
		<td onClick={onClick} class={style.name}>
			<a href={`/finance/category/stockdetail/symbol/${row.UniqueSymbol}`}
				data-tracking-text={row.UniqueSymbol}
			>
				{row.Name || row.ShortName || row.Symbol || ''}
			</a>
		</td>
	),

	competitors: ({ onClick, row }) => (
		<td onClick={onClick} class={style.name}>
			<a
				href={`/finance/category/stockdetail/symbol/${row.UniqueSymbol}`}
			>{`${row.Name} (${row.Symbol})`}</a>
		</td>
	),

	changeCompoundIcon: ({ onClick, row, change }) => (
		<td class={style.iconArrow + ` ${style[change.class]}`} />
	),

	changeCompound: ({ onClick, row, change }) => (
		change.price ?
			<td
				class={`${style.change} ${style.changeCompound} ${style[change.class]}`}
				onClick={onClick}
			>
				<span class={style.price}>
					{formatNumber(change.price, {
						Plus: true,
						Decimals: row.Price < 10 ? maxDecimals : minDecimals,
						Default: '0.00'
					})}
				</span>
				<span class={style.percent}>
					{formatNumber(change.percent, {
						Plus: true,
						Decimals: 2,
						Default: '0.00%',
						Suffix: '%'
					})}
				</span>
			</td>
			:
			<td class={`${style.change}`} onClick={onClick}>
				<Text id={`${INTL_NS}.unchanged`} />
			</td>
	),

	changePrice: ({ onClick, row, change }) => {
		let inner = '-';
		if (row.Price > 0) {
			inner = formatNumber(change.price, {
				Plus: true,
				Decimals: row.Price < 10 ? maxDecimals : minDecimals,
				Default: '0.00'
			});
		}
		return (<td class={cx(style.change, style[change.class])} onClick={onClick}>{inner}</td>);
	},

	changePercent: createChangePercentComponent('changePercent', 'PrevClose'),

	oneMonthChangePercent: createChangePercentComponent('oneMonthChangePercent', 'StartingPrice1M'),

	threeMonthChangePercent: createChangePercentComponent(
		'threeMonthChangePercent',
		'StartingPrice3M'
	),

	'52WeekChangePercent': createChangePercentComponent('52WeekChangePercent', 'StartingPrice52'),

	YTDChangePercent: createChangePercentComponent('YTDChangePercent', 'StartingPriceYTD'),

	trash: ({ onDelete, pos }) => {
		let trash = () => {
			onDelete(pos);
		};
		return (
			<td class={style.trash} onClick={trash}>
				<Icon name="trash" />
			</td>
		);
	},

	volume: ({ onClick, row }) => (
		<td class={cx(style.volume)} onClick={onClick}>
			{formatNumber(row.Volume, { Comma: true, Compact: true, Default: '0' })}
		</td>
	),

	dollarVolume: ({ onClick, row }) => (
		<td class={cx(style.dollarVolume)} onClick={onClick}>
			{formatNumber(row.Price * row.Volume, {
				Decimals: 2,
				Compact: true,
				Default: '$0.00',
				Prefix: '$'
			})}
		</td>
	),

	dollarVolumeChange: ({ onClick, row }) => (
		<td class={cx(style.change, style.dollarVolume)} onClick={onClick}>
			{formatNumber(row.Price * row.Volume, {
				Decimals: 2,
				Compact: true,
				Default: '$0.00',
				Prefix: '$'
			})}
		</td>
	)
};
